import { BomEditProposal, BomProposalEdit, BomProposalField, BomProposalValue } from '../types/bomProposal';
import { SmartBomComponentEdit } from '../types/bomVersions';
import type { ChatUiContext } from '../client/chatClient';

/**
 * Rules for Robbie's BOM proposal card — pure, so the card and its tests
 * agree on what "still applies" means.
 *
 * A proposal is a snapshot: the buyer may have edited a line, removed it, or
 * re-extracted the whole BOM since Robbie wrote it. Applying it blindly would
 * overwrite the buyer's newer value with Robbie's, so every line is checked
 * against the BOM as it is now before it can be ticked.
 */

export const PROPOSAL_FIELD_LABELS: Record<BomProposalField, string> = {
  name: 'Name',
  partNumber: 'Part number',
  mpn: 'MPN',
  manufacturer: 'Manufacturer',
  description: 'Description',
  specifications: 'Specifications',
  quantity: 'Qty',
  targetPrice: 'Target price',
};

/**
 * - `fresh`   — the line still holds the values Robbie saw; safe to apply.
 * - `changed` — someone changed one of those fields since; applying would
 *               overwrite that, so it starts unticked and cannot be ticked.
 * - `missing` — the line is no longer on the BOM.
 * - `done`    — the line already holds Robbie's values (applied elsewhere).
 */
export type ProposalLineStatus = 'fresh' | 'changed' | 'missing' | 'done';

/** A line as the RFQ payload carries it — any of the proposal's fields. */
export type CurrentBomLine = { componentId?: string } & Partial<Record<BomProposalField, unknown>>;

export interface ProposalAssessment {
  /** The proposal was for another BOM (re-extracted since) — nothing applies. */
  staleBom: boolean;
  lines: Record<string, ProposalLineStatus>;
}

/** Compared as a reader sees it: trimmed, inner whitespace collapsed (same rule as the tool). */
const norm = (value: unknown): string | null => {
  if (value === undefined || value === null) return null;
  const text = String(value).trim().replace(/\s+/g, ' ');
  return text === '' ? null : text;
};

const fields = (edit: BomProposalEdit) => Object.keys(edit.changes) as BomProposalField[];

export const lineStatus = (edit: BomProposalEdit, line: CurrentBomLine | undefined): ProposalLineStatus => {
  if (!line) return 'missing';
  if (fields(edit).every((f) => norm(line[f]) === norm(edit.changes[f]))) return 'done';
  if (fields(edit).some((f) => norm(line[f]) !== norm(edit.before[f]))) return 'changed';
  return 'fresh';
};

/**
 * @param currentBomId - the BOM the RFQ shows now; null while unknown, in
 *   which case the lines are not judged (nothing to judge them against).
 */
export const assessProposal = (
  proposal: BomEditProposal,
  currentBomId: string | null,
  currentLines: CurrentBomLine[] | null
): ProposalAssessment => {
  const staleBom = !!currentBomId && currentBomId !== proposal.bomId;
  const byId = new Map((currentLines ?? []).map((l) => [l.componentId, l]));
  const lines: Record<string, ProposalLineStatus> = {};
  proposal.edits.forEach((edit) => {
    lines[edit.componentId] = currentLines ? lineStatus(edit, byId.get(edit.componentId)) : 'fresh';
  });
  return { staleBom, lines };
};

/** How many field changes the given lines carry — the "Apply N changes" count. */
export const changeCount = (edits: BomProposalEdit[]): number =>
  edits.reduce((n, e) => n + fields(e).length, 0);

/** The ticked lines as the manual edit's `components`. */
export const toManualEdits = (edits: BomProposalEdit[]): SmartBomComponentEdit[] =>
  edits.map((e) => ({ componentId: e.componentId, ...e.changes }) as SmartBomComponentEdit);

export const formatProposalValue = (field: BomProposalField, value: BomProposalValue | null | undefined): string => {
  if (value === undefined || value === null || value === '') return '—';
  if (field === 'targetPrice' && typeof value === 'number') return value.toLocaleString('en-IN');
  return String(value);
};

/** The lines "Enhance with Robbie" is about; none = the whole BOM. */
export interface EnhanceSelection {
  componentIds: string[];
}

const ENHANCE_ASK =
  "fill missing part numbers, MPNs and quantities, flag duplicates, normalise descriptions, and tell me what you're unsure about.";

/**
 * What "Enhance with Robbie" sends: the request in the buyer's words, and
 * the view it came from so Robbie knows which lines "these" are.
 */
export const enhanceRequest = (
  lineCount: number,
  selection?: EnhanceSelection
): { text: string; uiContext: ChatUiContext } => {
  const picked = selection?.componentIds.filter(Boolean) ?? [];
  const scope =
    picked.length > 0
      ? `these ${picked.length} ${picked.length === 1 ? 'line' : 'lines'}`
      : `all ${lineCount} ${lineCount === 1 ? 'line' : 'lines'}`;
  return {
    text: `Review ${scope}: ${ENHANCE_ASK}`,
    uiContext: { view: 'extracted-bom', ...(picked.length > 0 ? { componentIds: picked } : {}) },
  };
};

/** The row heading: the line's name, else its part number, else its id. */
export const lineLabel = (edit: BomProposalEdit): string => edit.name || edit.partNumber || edit.componentId;
